import React from 'react';
import styled from 'styled-components';
import { Content, Stack } from '@kinesis/bungle';
import { GpcDetailTable as GpcDetailTableData } from '@/types';
import { Title } from '@/components/title';
import { renderValueOptional, renderUnit } from '@/data/units';
import { renderScopeNumber } from '@/data/scope';

// NOTE: styles stolen from bungle nursery table, but adapted to
// a very custom grid layout that works only for this table

const alignXMap = new Map([
  ['left', 'start'],
  ['center', 'center'],
  ['right', 'end'],
]);
const paddingMap = new Map([['none', 0]]);

const TableRow = styled<any>('div')(
  ({ theme }: any) => ({
    display: 'grid',
    gridColumn: '1 / -1',
    gridTemplateColumns: 'subgrid',
    '&:hover': {
      backgroundColor: theme.color.gray3,
    },
  }),
  ({ $gridRow }: any) => ({
    gridRow: $gridRow,
  }),
);

const TableCell = styled<any>('div')(
  () => ({
    alignItems: 'center',
    display: 'grid',
    '&[role="columnheader"]': {
      backgroundColor: '#fafafa',
      fontWeight: 'bold',
    },
  }),
  ({ $heading }: any) =>
    $heading
      ? {
          fontWeight: 'bold',
        }
      : undefined,
  ({ $alignX }: any) =>
    alignXMap.has($alignX)
      ? {
          justifyContent: alignXMap.get($alignX),
        }
      : {
          justifyContent: 'stretch',
        },
  ({ $padding }: any) =>
    paddingMap.has($padding)
      ? {
          padding: paddingMap.get($padding),
        }
      : {
          padding: '0 12px',
        },
  ({ theme, $showGridLines }: any) =>
    $showGridLines
      ? {
          '&:not(:first-child)': {
            borderLeft: `1px solid ${theme.borderColor}`,
          },
          [`${TableRow}:not(:first-child) &`]: {
            borderTop: `1px solid ${theme.borderColor}`,
          },
        }
      : undefined,
  ({ $gridRow }: any) => ({
    gridRow: $gridRow,
  }),
  ({ $gridColumn }: any) => ({
    gridColumn: $gridColumn,
  }),
);
const TableWrapper = styled<any>('div')(
  ({ theme }: any) => ({
    alignItems: 'stretch',
    display: 'grid',
    fontSize: theme.fontSize.medium,
    gap: 1,
    gridAutoRows: 32,
    whiteSpace: 'nowrap',
    width: '100%',
  }),
  ({ $gridTemplateColumns }: any) => ({
    gridTemplateColumns: $gridTemplateColumns,
  }),
);

const GpcDetailTable = (props: GpcDetailTableProps) => {
  const { data } = props;

  return (
    <Stack space='medium'>
      <Title>GPC Detail Table</Title>
      <Content
        background='white'
        padding='medium'
        border
        paddingMode='equal'
        borderRadiusSize='small'
      >
        <Content border>
          <TableWrapper
            role='table'
            $gridTemplateColumns='1fr 1fr 10fr 1fr 1fr'
          >
            <TableRow role='row' $gridRow='span 1'>
              <TableCell
                role='columnheader'
                $showGridLines
                $gridRow='1 / span 1'
                $gridColumn='1'
              >
                GPC ref
              </TableCell>
              <TableCell
                role='columnheader'
                $showGridLines
                $gridRow='1 / span 1'
                $gridColumn='2'
              >
                Scope
              </TableCell>
              <TableCell
                role='columnheader'
                $showGridLines
                $gridRow='1 / span 1'
                $gridColumn='3'
              >
                GHG emissions source
              </TableCell>
              <TableCell
                role='columnheader'
                $showGridLines
                $gridRow='1 / span 1'
                $gridColumn='4'
              >
                Notation keys
              </TableCell>
              <TableCell
                role='columnheader'
                $showGridLines
                $gridRow='1 / span 1'
                $gridColumn='5'
              >
                {renderUnit(data.unit)}
              </TableCell>
            </TableRow>
            {data.references.map((r) => (
              <TableRow key={r.reference}>
                <TableCell
                  role={r.scope ? undefined : 'columnheader'}
                  $gridRow='1 / span 1'
                  $showGridLines
                >
                  {r.reference}
                </TableCell>
                <TableCell
                  role={r.scope ? undefined : 'columnheader'}
                  $gridRow='1 / span 1'
                  $showGridLines
                >
                  {r.scope ? renderScopeNumber(r.scope) : null}
                </TableCell>
                <TableCell
                  role={r.scope ? undefined : 'columnheader'}
                  $gridRow='1 / span 1'
                  $showGridLines
                >
                  {r.description}
                </TableCell>
                <TableCell
                  role={r.scope ? undefined : 'columnheader'}
                  $gridRow='1 / span 1'
                  $showGridLines
                >
                  {r.notation}
                </TableCell>
                <TableCell
                  role={r.scope ? undefined : 'columnheader'}
                  $gridRow='1 / span 1'
                  $showGridLines
                >
                  {renderValueOptional(r.emissions, data.unit)}
                </TableCell>
                <TableCell
                  role={r.scope ? undefined : 'columnheader'}
                  $gridRow='1 / span 1'
                  $showGridLines
                >
                  {r.activity}
                </TableCell>
                <TableCell
                  role={r.scope ? undefined : 'columnheader'}
                  $gridRow='1 / span 1'
                  $showGridLines
                >
                  {r.factor}
                </TableCell>
              </TableRow>
            ))}
          </TableWrapper>
        </Content>
      </Content>
    </Stack>
  );
};

export type GpcDetailTableProps = {
  data: GpcDetailTableData;
};

export { GpcDetailTable };
